import { CUSTOM_ELEMENTS_SCHEMA, Component, inject } from '@angular/core'
import { SimplebarAngularModule } from 'simplebar-angular'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import {
  NgbCollapse,
  NgbCollapseModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap'
import { CommonModule } from '@angular/common'
import { findAllParent, findMenuItem } from '../../helpers/utils'
import { LogoBoxComponent } from '@/app/components/logo-box.component'
import { MENU, type MenuItem } from '@/app/common/menu-meta'
import { changesidebarsize } from '@/app/store/layout/layout-action'
import { Store } from '@ngrx/store'
import { getSidebarsize } from '@/app/store/layout/layout-selector'
import { basePath } from '@/app/common/constants'
import { CommonService } from '@/app/core/services/common.service'
import { LogoBox2Component } from '@/app/components/logo-box-2.component'
import { CommonDataStorage } from '@/app/common/commonDataStore'

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [SimplebarAngularModule, RouterModule, NgbCollapseModule, CommonModule, NgbTooltipModule, LogoBox2Component,],
  templateUrl: './sidebar.component.html',
  styles: ``,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SidebarComponent {
  menuItems: MenuItem[] = []
  activeMenuItems: string[] = []
  store = inject(Store);
  router = inject(Router)
  trimmedURL = this.router.url?.replaceAll(basePath !== '' ? basePath + '/' : '', '/')
  public logoUrl = '';
  public commonService = inject(CommonService);
  public commonData = inject(CommonDataStorage);


  constructor() {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.trimmedURL = this.router.url?.replaceAll(basePath !== '' ? basePath + '/' : '', '/')
        this._activateMenu()
        setTimeout(() => { this.scrollToActive() }, 200)
      }
    })
  }

  ngOnInit(): void {
    this.logoUrl = this.commonService.getTenantLogo();
    this.initMenu()
  }

  initMenu(): void {
    this.commonData.getPermissions().subscribe((perms: any[]) => {
      const allowed = this.getPermissionCodes(perms);
      this.menuItems = this.filterMenu(MENU, allowed);
    });
  }


  ngAfterViewInit() {
    setTimeout(() => { this._activateMenu() })
    setTimeout(() => { this.scrollToActive() }, 200)
  }

  scrollToActive(): void {
    const activatedItem = document.querySelector('.nav-item li a.active')
    if (activatedItem) {
      const simplebarContent = document.querySelector('.main-nav .simplebar-content-wrapper')
      if (simplebarContent) {
        const activatedItemRect = activatedItem.getBoundingClientRect()
        const simplebarContentRect = simplebarContent.getBoundingClientRect()
        const activatedItemOffsetTop =
          activatedItemRect.top + simplebarContent.scrollTop
        const centerOffset =
          activatedItemOffsetTop -
          simplebarContentRect.top -
          simplebarContent.clientHeight / 2 +
          activatedItemRect.height / 2
        this.scrollTo(simplebarContent, centerOffset, 600)
      }
    }
  }

  easeInOutQuad(t: number, b: number, c: number, d: number): number {
    t /= d / 2
    if (t < 1) return (c / 2) * t * t + b
    t--
    return (-c / 2) * (t * (t - 2) - 1) + b
  }

  scrollTo(element: Element, to: number, duration: number): void {
    const start = element.scrollTop
    const change = to - start
    const increment = 20
    let currentTime = 0

    const animateScroll = () => {
      currentTime += increment
      const val = this.easeInOutQuad(currentTime, start, change, duration)
      element.scrollTop = val
      if (currentTime < duration) {
        setTimeout(animateScroll, increment)
      }
    }
    animateScroll()
  }

  _activateMenu(): void {
    const div = document.querySelector('.navbar-nav')
    let matchingMenuItem = null
    if (div) {
      let items: any = div.getElementsByClassName('nav-link-ref')
      for (let i = 0; i < items.length; ++i) {
        if (
          this.trimmedURL === items[i].pathname ||
          (this.trimmedURL.startsWith('/invoice/') &&
            items[i].pathname === '/invoice/RB6985') ||
          (this.trimmedURL.startsWith('/ecommerce/product/') &&
            items[i].pathname === '/ecommerce/product/1')
        ) {
          matchingMenuItem = items[i]
          break
        }
      }

      if (matchingMenuItem) {
        const mid = matchingMenuItem.getAttribute('aria-controls')
        const activeMt = findMenuItem(this.menuItems, mid)

        if (activeMt) {
          const matchingObjs = [
            activeMt['key'],
            ...findAllParent(this.menuItems, activeMt),
          ]

          this.activeMenuItems = matchingObjs
          this.menuItems.forEach((menu: MenuItem) => {
            menu.collapsed = !matchingObjs.includes(menu.key!)
          })
        }
      }
    }
  }

  hasSubmenu(menu: MenuItem): boolean { return menu.subMenu ? true : false }

  toggleMenuItem(menuItem: MenuItem, collapse: NgbCollapse): void {
    collapse.toggle()
    let openMenuItems: string[]
    if (!menuItem.collapsed) {
      openMenuItems = [
        menuItem['key'],
        ...findAllParent(this.menuItems, menuItem),
      ]
      this.menuItems.forEach((menu: MenuItem) => {
        if (!openMenuItems.includes(menu.key!)) {
          menu.collapsed = true
        }
      })
    }
  }

  changeSidebarSize() {
    let size = document.documentElement.getAttribute('data-menu-size')
    if (size == 'sm-hover') {
      size = 'sm-hover-active'
    } else {
      size = 'sm-hover'
    }
    this.store.dispatch(changesidebarsize({ size }))
    this.store.select(getSidebarsize).subscribe((size) => {
      document.documentElement.setAttribute('data-menu-size', size)
    })
  }

  getPermissionCodes(perms: any[]): string[] { return perms.map(p => p.functionality__code); }

  hasAccess(menu: MenuItem, allowed: string[]): boolean {
    if (menu.alwaysVisible) return true;
    if (!menu.access || menu.access.length === 0) return false;
    return menu.access.some(code => allowed.includes(code));
  }

  filterMenu(items: MenuItem[], allowed: string[]): MenuItem[] {
    return items.reduce((acc: MenuItem[], raw) => {
      const menu = { ...raw };
      if (menu.subMenu) { menu.subMenu = this.filterMenu(menu.subMenu, allowed); }
      const allowedSelf = this.hasAccess(menu, allowed);
      const hasChildren = menu.subMenu && menu.subMenu.length > 0;
      if (menu.isTitle) {
        if (hasChildren) acc.push(menu);
        return acc;
      }
      if (allowedSelf || hasChildren) acc.push(menu);
      return acc;
    }, []);
  }
}
